import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Play, Pause, Square, RotateCcw, ChevronLeft, ChevronRight, X, List, PenLine, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { JACE, TYPE_LABELS } from "./shared";

// Training Play — live coaching interface (mobile-first, geen sidebar)
export default function JacePlay() {
  const { sessionId, itemId } = useParams();
  const engineMode = !sessionId; // direct afspelen vanuit de Engine-bibliotheek (geen sessie, niets wordt opgeslagen)
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [step, setStep] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [timerOn, setTimerOn] = useState(true);
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [showExit, setShowExit] = useState(false);
  const [note, setNote] = useState(null);
  const wakeRef = useRef(null);

  useEffect(() => {
    const req = sessionId ? api.get(`/jace/sessions/${sessionId}`) : api.get(`/jace/library/${itemId}`);
    req.then(({ data }) => {
      if (sessionId) {
        setS(data);
        const ps = data.play_state;
        if (ps && typeof ps.step === "number") setStep(Math.min(Math.max(0, ps.step), Number.MAX_SAFE_INTEGER));
      } else {
        // Engine-item normaliseren naar de sessie-vorm die de player verwacht
        setS({
          title: data.name,
          blocks: (data.blocks || []).map((b) => ({
            name: b.name, duration: b.duration, coach_note: b.notes || "",
            exercises: (b.exercises || []).map((e) => typeof e === "string"
              ? { name: e, duration: 0, coach_note: "", detail: "" }
              : { name: e.name, duration: e.duration, coach_note: e.notes || "", detail: e.reps ? String(e.reps) : "" }),
          })),
        });
      }
    }).catch(() => setS(false));
    // Wake lock — houd scherm wakker waar ondersteund
    if (navigator.wakeLock) navigator.wakeLock.request("screen").then((w) => (wakeRef.current = w)).catch(() => {});
    return () => { if (wakeRef.current) wakeRef.current.release().catch(() => {}); };
  }, [sessionId, itemId]);

  // Platte stappenlijst: blokken met exercises → iedere exercise een stap; blok zonder exercises = 1 stap
  const steps = useMemo(() => {
    if (!s) return [];
    const out = [];
    (s.blocks || []).forEach((b) => {
      const exs = b.exercises || [];
      if (exs.length === 0) out.push({ block: b.name, name: b.name, duration: b.duration || 0, note: b.coach_note || "", detail: "" });
      exs.forEach((e) => out.push({ block: b.name, name: e.name, duration: e.duration || b.duration || 0, note: e.coach_note || b.coach_note || "", detail: e.detail || "" }));
    });
    return out;
  }, [s]);

  const cur = steps[step];

  // timer reset bij stapwissel
  useEffect(() => {
    setSeconds((cur?.duration || 0) * 60);
    setRunning(false);
  }, [step, sessionId]); // eslint-disable-line

  // timer initialiseren zodra de sessie geladen is (steps waren eerst leeg)
  const initRef = useRef(false);
  useEffect(() => {
    if (!initRef.current && steps.length) {
      initRef.current = true;
      setSeconds((steps[step]?.duration || 0) * 60);
    }
  }, [steps]); // eslint-disable-line

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setSeconds((x) => {
        if (x <= 1) {
          clearInterval(t);
          if (autoAdvance && step < steps.length - 1) { setStep(step + 1); return 0; }
          setRunning(false);
          return 0;
        }
        return x - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [running, step, autoAdvance, steps.length]);

  const fmt = (sec) => `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;

  const quickNote = async () => {
    const text = (note || "").trim();
    if (!text) return;
    try {
      await api.post(`/jace/sessions/${sessionId}/quick-note`, { note: text });
      toast.success("Quick note opgeslagen");
      setNote(null);
    } catch { toast.error("Notitie opslaan mislukt"); }
  };

  const saveExit = async () => {
    if (engineMode) { nav("/jace/engine"); return; }
    try { await api.post(`/jace/sessions/${sessionId}/play-state`, { step, at: new Date().toISOString() }); } catch {}
    nav(`/jace/sessions/${sessionId}`);
  };

  if (s === null) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><span className="chip text-[#A3A3A3]">JACE // LOADING…</span></div>;
  if (s === false) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><span className="chip text-[#4A4A4A]">Sessie niet gevonden.</span></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F3F0] flex flex-col grain grain-dark" data-testid="jace-play">
      {/* Top */}
      <div className="flex items-center justify-between px-5 py-4 line-b line-dark">
        <div>
          <div className="chip" style={{ color: JACE }}>{engineMode ? "ENGINE // PLAY" : "JACE // PLAY"}</div>
          <div className="font-display uppercase text-xl mt-0.5">{s.title}</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="chip text-[#A3A3A3]" data-testid="jace-play-progress">{String(step + 1).padStart(2, "0")} / {String(steps.length).padStart(2, "0")}</span>
          <button onClick={() => setShowExit(true)} className="w-10 h-10 border border-[#F5F3F0]/25 flex items-center justify-center" title="Exit Training Play" data-testid="jace-play-exit"><LogOut size={15}/></button>
        </div>
      </div>

      {/* Current */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center py-8">
        <div className="chip text-[#A3A3A3] mb-2" data-testid="jace-play-block">{cur?.block}</div>
        <div className="font-display uppercase text-4xl md:text-6xl leading-tight max-w-2xl" data-testid="jace-play-exercise">{cur?.name || "—"}</div>
        {cur?.detail && <div className="chip mt-3" style={{ color: JACE }}>{cur.detail}</div>}
        {timerOn && (
          <div className="font-display text-7xl md:text-8xl mt-6 tabular-nums" style={{ color: running ? JACE : "#F5F3F0" }} data-testid="jace-play-timer">{fmt(seconds)}</div>
        )}
        {cur?.note && (
          <div className="mt-6 max-w-md">
            <div className="chip text-[#D4A93A]">COACH NOTE //</div>
            <div className="text-[#DDD6CB] text-sm mt-1 uppercase tracking-wide" data-testid="jace-play-note">{cur.note}</div>
          </div>
        )}
        {/* Volgende */}
        {step < steps.length - 1 && (
          <div className="chip text-[#4A4A4A] mt-8" data-testid="jace-play-next">NEXT // {steps[step + 1].name}</div>
        )}
      </div>

      {/* Extra acties */}
      <div className="flex justify-center gap-2 pb-3 px-4 flex-wrap">
        <button onClick={() => setShowPlan(true)} className="chip px-3 py-2 border border-[#F5F3F0]/20 text-[#A3A3A3] inline-flex items-center gap-1.5" data-testid="jace-play-plan"><List size={12}/> VIEW SESSION</button>
        {!engineMode && (
          <button onClick={() => setNote("")} className="chip px-3 py-2 border border-[#F5F3F0]/20 text-[#A3A3A3] inline-flex items-center gap-1.5" data-testid="jace-play-quicknote"><PenLine size={12}/> QUICK NOTE</button>
        )}
        <button onClick={() => setTimerOn(!timerOn)} className="chip px-3 py-2 border transition-colors" style={timerOn ? { borderColor: JACE, color: JACE } : { borderColor: "#F5F3F033", color: "#4A4A4A" }} data-testid="jace-play-timer-toggle">TIMER {timerOn ? "ON" : "OFF"}</button>
        <button onClick={() => setAutoAdvance(!autoAdvance)} className="chip px-3 py-2 border transition-colors" style={autoAdvance ? { borderColor: JACE, color: JACE } : { borderColor: "#F5F3F033", color: "#4A4A4A" }} data-testid="jace-play-auto">AUTO ADVANCE {autoAdvance ? "ON" : "OFF"}</button>
      </div>

      {/* Sticky controls */}
      <div className="sticky bottom-0 bg-[#0A0A0A] border-t border-[#F5F3F0]/15 px-4 py-3 flex items-center justify-between gap-2" data-testid="jace-play-controls">
        <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="h-14 px-5 border border-[#F5F3F0]/25 flex items-center gap-2 chip disabled:opacity-30" data-testid="jace-play-prev"><ChevronLeft size={16}/> PREV</button>
        <div className="flex items-center gap-2">
          <button onClick={() => setSeconds((cur?.duration || 0) * 60)} className="w-14 h-14 border border-[#F5F3F0]/25 flex items-center justify-center" title="Reset" data-testid="jace-play-reset"><RotateCcw size={16}/></button>
          <button onClick={() => setRunning(!running)} className="w-20 h-14 flex items-center justify-center" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-play-toggle">{running ? <Pause size={20}/> : <Play size={20}/>}</button>
          <button onClick={() => { setRunning(false); setSeconds(0); }} className="w-14 h-14 border border-[#F5F3F0]/25 flex items-center justify-center" title="Stop (alleen timer)" data-testid="jace-play-stop"><Square size={15}/></button>
        </div>
        <div className="flex flex-col gap-1">
          <button onClick={() => setSeconds((x) => x + 30)} className="chip border border-[#F5F3F0]/20 px-2 py-1 text-[#A3A3A3]" data-testid="jace-play-plus30">+30 SEC</button>
          <button onClick={() => setSeconds((x) => x + 60)} className="chip border border-[#F5F3F0]/20 px-2 py-1 text-[#A3A3A3]" data-testid="jace-play-plus60">+1 MIN</button>
        </div>
        <button onClick={() => setStep(Math.min(steps.length - 1, step + 1))} disabled={step >= steps.length - 1} className="h-14 px-5 border border-[#F5F3F0]/25 flex items-center gap-2 chip disabled:opacity-30" data-testid="jace-play-next-btn">NEXT <ChevronRight size={16}/></button>
      </div>

      {/* VIEW SESSION overlay */}
      {showPlan && (
        <div className="fixed inset-0 z-50 bg-[#0A0A0A]/97 flex flex-col" data-testid="jace-play-planview">
          <div className="flex items-center justify-between px-5 py-4 line-b line-dark">
            <div className="chip" style={{ color: JACE }}>// VIEW SESSION</div>
            <button onClick={() => setShowPlan(false)} className="w-10 h-10 border border-[#F5F3F0]/25 flex items-center justify-center"><X size={15}/></button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {steps.map((st, i) => (
              <button key={i} onClick={() => { setStep(i); setShowPlan(false); }}
                className="w-full text-left py-3 border-b border-[#F5F3F0]/10 flex items-center gap-4"
                data-testid={`jace-plan-step-${i}`}>
                <span className="chip w-8" style={{ color: i === step ? JACE : i < step ? "#4ADE80" : "#4A4A4A" }}>{String(i + 1).padStart(2, "0")}</span>
                <span className={`flex-1 font-display uppercase text-lg ${i === step ? "" : "text-[#A3A3A3]"}`}>{st.name}</span>
                <span style={{ color: i < step ? "#4ADE80" : i === step ? JACE : "#4A4A4A" }}>{i < step ? "✓" : i === step ? "●" : "○"}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* QUICK NOTE modal */}
      {note !== null && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setNote(null)}>
          <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="jace-quicknote-modal">
            <div className="chip text-[#A3A3A3] mb-3">+ QUICK NOTE</div>
            <textarea autoFocus rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="bv. Group struggled with lateral movement." className="editorial-input dark w-full" data-testid="jace-quicknote-input" />
            <div className="flex gap-3 mt-4">
              <button onClick={quickNote} className="flex-1 py-3 chip" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-quicknote-save">Opslaan</button>
              <button onClick={() => setNote(null)} className="btn-ghost-dark">Annuleer</button>
            </div>
          </div>
        </div>
      )}

      {/* EXIT modal */}
      {showExit && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowExit(false)}>
          <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="jace-exit-modal">
            <div className="chip text-[#A3A3A3] mb-1">// EXIT TRAINING PLAY</div>
            <div className="font-display uppercase text-2xl mb-5">{s.title}</div>
            <div className="space-y-2">
              {engineMode ? (
                <button onClick={saveExit} className="w-full py-3.5 chip" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-exit-engine-back">TERUG NAAR ENGINE</button>
              ) : (
                <>
                  <button onClick={saveExit} className="w-full py-3.5 chip border border-[#F5F3F0]/25 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors" data-testid="jace-exit-save">SAVE & EXIT</button>
                  <button onClick={() => nav(`/jace/sessions/${sessionId}/review`)} className="w-full py-3.5 chip" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-exit-complete">COMPLETE SESSION → REVIEW</button>
                </>
              )}
              <button onClick={() => setShowExit(false)} className="w-full py-3.5 chip text-[#A3A3A3]" data-testid="jace-exit-cancel">CANCEL</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
